/**
 * Achievement & Badge Service
 *
 * Awards badges for milestones and tracks user streaks.
 */

import { Badge, BadgeType, Streak } from '@/types/models';

/**
 * Badge definitions with metadata
 */
export const BADGE_DEFINITIONS: Record<BadgeType, { name: string; description: string }> = {
  first_lesson: {
    name: 'First Dive',
    description: 'Complete your first lesson',
  },
  first_course: {
    name: 'Course Navigator',
    description: 'Complete your first full course',
  },
  perfect_score: {
    name: 'Perfect Execution',
    description: 'Achieve 100% on a lesson quiz',
  },
  streak_7: {
    name: 'Week Warrior',
    description: '7-day learning streak',
  },
  streak_30: {
    name: 'Month Master',
    description: '30-day learning streak',
  },
  lessons_10: {
    name: 'Dedicated Learner',
    description: 'Complete 10 lessons',
  },
  lessons_25: {
    name: 'Knowledge Seeker',
    description: 'Complete 25 lessons',
  },
  lessons_50: {
    name: 'Education Elite',
    description: 'Complete 50 lessons',
  },
  master_diver: {
    name: 'Master Diver',
    description: 'Achieve Master level on 10 lessons',
  },
};

/**
 * Create a badge instance
 */
export function createBadge(type: BadgeType, _userId: string): Badge {
  const definition = BADGE_DEFINITIONS[type];

  return {
    id: `${_userId}_${type}_${Date.now()}`,
    type,
    name: definition.name,
    description: definition.description,
    earnedAt: Date.now(),
  };
}

/**
 * Check for new badges based on progress
 *
 * Returns array of badge types that should be awarded
 */
export function checkForNewBadges(
  _userId: string,
  stats: {
    lessonsCompleted: number;
    coursesCompleted: number;
    hasPerfectScore: boolean;
    currentStreak: number;
    masterLevelCount: number;
  },
  existingBadges: Badge[]
): BadgeType[] {
  const earnedTypes = new Set(existingBadges.map(b => b.type));
  const newBadges: BadgeType[] = [];

  // First lesson
  if (stats.lessonsCompleted >= 1 && !earnedTypes.has('first_lesson')) {
    newBadges.push('first_lesson');
  }

  // First course
  if (stats.coursesCompleted >= 1 && !earnedTypes.has('first_course')) {
    newBadges.push('first_course');
  }

  // Perfect score
  if (stats.hasPerfectScore && !earnedTypes.has('perfect_score')) {
    newBadges.push('perfect_score');
  }

  // Streak badges
  if (stats.currentStreak >= 7 && !earnedTypes.has('streak_7')) {
    newBadges.push('streak_7');
  }
  if (stats.currentStreak >= 30 && !earnedTypes.has('streak_30')) {
    newBadges.push('streak_30');
  }

  // Lesson milestones
  if (stats.lessonsCompleted >= 10 && !earnedTypes.has('lessons_10')) {
    newBadges.push('lessons_10');
  }
  if (stats.lessonsCompleted >= 25 && !earnedTypes.has('lessons_25')) {
    newBadges.push('lessons_25');
  }
  if (stats.lessonsCompleted >= 50 && !earnedTypes.has('lessons_50')) {
    newBadges.push('lessons_50');
  }

  // Master diver
  if (stats.masterLevelCount >= 10 && !earnedTypes.has('master_diver')) {
    newBadges.push('master_diver');
  }

  return newBadges;
}

/**
 * Initialize streak for a new user
 */
export function createInitialStreak(): Streak {
  const today = getDateOnly(Date.now());

  return {
    currentStreak: 1,
    longestStreak: 1,
    lastActivityDate: today,
    totalActiveDays: 1,
  };
}

/**
 * Update streak based on new activity
 */
export function updateStreak(currentStreak: Streak, activityTimestamp: number): Streak {
  const today = getDateOnly(activityTimestamp);
  const lastDate = currentStreak.lastActivityDate;
  const daysDiff = Math.floor((today - lastDate) / (24 * 60 * 60 * 1000));

  let newStreak: Streak;

  if (daysDiff === 0) {
    // Same day - no change to streak
    newStreak = currentStreak;
  } else if (daysDiff === 1) {
    // Consecutive day - increment streak
    const newCurrentStreak = currentStreak.currentStreak + 1;
    newStreak = {
      currentStreak: newCurrentStreak,
      longestStreak: Math.max(newCurrentStreak, currentStreak.longestStreak),
      lastActivityDate: today,
      totalActiveDays: currentStreak.totalActiveDays + 1,
    };
  } else {
    // Streak broken - reset to 1
    newStreak = {
      currentStreak: 1,
      longestStreak: currentStreak.longestStreak,
      lastActivityDate: today,
      totalActiveDays: currentStreak.totalActiveDays + 1,
    };
  }

  return newStreak;
}

/**
 * Get date-only timestamp (midnight UTC)
 */
function getDateOnly(timestamp: number): number {
  const date = new Date(timestamp);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * Check if streak is at risk (last activity was yesterday)
 */
export function isStreakAtRisk(streak: Streak): boolean {
  const today = getDateOnly(Date.now());
  const daysDiff = Math.floor((today - streak.lastActivityDate) / (24 * 60 * 60 * 1000));

  return daysDiff === 1 && streak.currentStreak > 0;
}

/**
 * Get user statistics for badge checking
 */
export function calculateUserStats(
  completedLessons: number,
  completedCourses: number,
  hasAnyPerfectScore: boolean,
  currentStreak: number,
  masterLevelLessons: number
) {
  return {
    lessonsCompleted: completedLessons,
    coursesCompleted: completedCourses,
    hasPerfectScore: hasAnyPerfectScore,
    currentStreak,
    masterLevelCount: masterLevelLessons,
  };
}
